import api from './api';

export type DayOfWeek = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export interface Lesson {
  id?: string;
  schoolId?: string;
  classeId: string;
  teacherId: string;
  courseId: string;
  dayOfWeek: DayOfWeek;
  startTime: string; // Format: "HH:mm" (e.g., "08:00")
  endTime: string;   // Format: "HH:mm" (e.g., "09:30")
  room?: string;
  creationDatetime?: string;
  lastModifiedDatetime?: string;
}

export interface LessonResponse {
  id: string;
  schoolId: string;
  classeId: string;
  className?: string;
  teacherId: string;
  teacherName?: string;
  courseId: string;
  courseName?: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  room?: string;
  creationDatetime?: string;
  lastModifiedDatetime?: string;
}

export interface ConflictWarning {
  type: 'TEACHER' | 'CLASS' | 'ROOM';
  message: string;
  blocking: boolean;
}

export interface LessonValidationResponse {
  valid: boolean;
  conflicts: ConflictWarning[];
}

export interface LessonRequest {
  classeId: string;
  teacherId: string;
  courseId: string;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  room?: string;
}

export const lessonService = {
  // Get all lessons for a specific class (student/admin timetable view)
  getLessonsByClass: async (classeId: string): Promise<LessonResponse[]> => {
    const response = await api.get<LessonResponse[]>(`/{schoolId}/lessons/class/${classeId}`);
    return response.data;
  },

  // Get all lessons for a specific teacher (teacher's personal timetable)
  getLessonsByTeacher: async (teacherId: string): Promise<LessonResponse[]> => {
    const response = await api.get<LessonResponse[]>(`/{schoolId}/lessons/teacher/${teacherId}`);
    return response.data;
  },

  // Get a single lesson by ID
  getLessonById: async (id: string): Promise<LessonResponse> => {
    const response = await api.get<LessonResponse>(`/{schoolId}/lessons/${id}`);
    return response.data;
  },

  // Get all lessons for the school (admin overview)
  getAllLessons: async (): Promise<LessonResponse[]> => {
    const response = await api.get<LessonResponse[]>('/{schoolId}/lessons');
    return response.data;
  },

  // Create a new lesson
  createLesson: async (lesson: LessonRequest): Promise<LessonResponse> => {
    const response = await api.post<LessonResponse>('/{schoolId}/lessons', lesson);
    return response.data;
  },

  // Update an existing lesson
  updateLesson: async (id: string, lesson: LessonRequest): Promise<LessonResponse> => {
    const response = await api.put<LessonResponse>(`/{schoolId}/lessons/${id}`, lesson);
    return response.data;
  },

  // Delete a lesson
  deleteLesson: async (id: string): Promise<void> => {
    await api.delete(`/{schoolId}/lessons/${id}`);
  },

  // Validate a lesson for conflicts (pre-validation)
  validateLesson: async (lesson: LessonRequest, excludeLessonId?: string): Promise<LessonValidationResponse> => {
    const params = excludeLessonId ? `?excludeLessonId=${excludeLessonId}` : '';
    const response = await api.post<LessonValidationResponse>(
      `/{schoolId}/lessons/validate${params}`,
      lesson
    );
    return response.data;
  },
};

